import { useCallback, useEffect, useState } from 'react'

import { useQuery } from 'modules/core/hooks/useQuery'
import { F0Box, F0Button, F0Card, F0Text } from '@factorialco/f0-react'
import type { GoalsTabMetricGroupFragment } from 'generated/resources/graphql'
import { PerformanceGoalsSidepanelDocument } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import { GoalGroupCard } from './GoalGroupCard'

const PAGE_SIZE = 10

const EmptyState = () => (
  <F0Box
    display="flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    gap="md"
    padding="xl"
  >
    <F0Text
      variant="body"
      content={i18n.t(
        'performance_process.reviews.answer_evaluation.goals_section.empty_state.title'
      )}
    />
    <F0Text
      variant="description"
      content={i18n.t(
        'performance_process.reviews.answer_evaluation.goals_section.empty_state.description'
      )}
    />
  </F0Box>
)

const LoadingState = () => (
  <F0Box display="flex" flexDirection="column" gap="md">
    <F0Card.Skeleton key="goals-skeleton-1" compact />
    <F0Card.Skeleton key="goals-skeleton-2" compact />
  </F0Box>
)

export const GoalsTab = ({ employeeId }: { employeeId: string }) => {
  const [goals, setGoals] = useState<GoalsTabMetricGroupFragment[]>([])
  const [offsetPage, setOffsetPage] = useState(1)
  const [expandedGoalsIds, setExpandedGoalsIds] = useState<Set<string>>(new Set())
  const [hasNextPage, setHasNextPage] = useState(false)

  const { loading, data } = useQuery(PerformanceGoalsSidepanelDocument, {
    variables: { employeeId, first: PAGE_SIZE, offsetPage },
    fetchPolicy: 'network-only',
  })

  const isLoadingMore = loading && goals.length > 0

  useEffect(() => {
    const connection = data?.goals.metricGroupsConnection
    if (!connection) return

    setHasNextPage(connection.pageInfo.hasNextPage)

    const nodes = connection.nodes
    if (!nodes.length) return

    setGoals((prev) => {
      const existingIds = new Set(prev.map((g) => g.id))
      const unique = nodes.filter((g) => !existingIds.has(g.id))
      if (unique.length === 0) return prev
      return [...prev, ...unique]
    })

    const expandableIds = nodes
      .filter((group) => group.metricsConnection?.nodes?.length)
      .map((group) => group.id)

    if (expandableIds.length > 0) {
      setExpandedGoalsIds((prev) => {
        const next = new Set(prev)
        expandableIds.forEach((id) => next.add(id))
        return next
      })
    }
  }, [data])

  const handleLoadMore = useCallback(() => {
    setOffsetPage((prev) => prev + 1)
  }, [])

  const handleToggle = useCallback((groupId: string) => {
    setExpandedGoalsIds((prev) => {
      const next = new Set(prev)
      if (next.has(groupId)) {
        next.delete(groupId)
      } else {
        next.add(groupId)
      }
      return next
    })
  }, [])

  if (loading && goals.length === 0) {
    return <LoadingState />
  }

  if (!loading && goals.length === 0) {
    return <EmptyState />
  }

  return (
    <F0Box display="flex" flexDirection="column" gap="xl">
      {goals.map((group) => (
        <GoalGroupCard
          key={group.id}
          group={group}
          isExpanded={expandedGoalsIds.has(group.id)}
          onToggle={() => handleToggle(group.id)}
        />
      ))}

      {hasNextPage && (
        <F0Box display="flex" justifyContent="center" paddingTop="md">
          <F0Button
            label={i18n.t('performance_process.reviews.answer_evaluation.goals_section.load_more')}
            variant="outline"
            onClick={handleLoadMore}
            loading={isLoadingMore}
          />
        </F0Box>
      )}
    </F0Box>
  )
}
